import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpHeaders } from '@angular/common/http';
import { MembersPortalService } from './members-portal.service';
import { getJwtToken } from '../store/userPreferences';
import { EXTERNAL_APIS } from '../constants';
import { AppState } from '../store/appState';
import { Store } from '@ngrx/store';
import { getTokenKeyring } from '../store/wallet';
import { Logger } from './logger.service';
import { take, timeout } from 'rxjs/operators';
interface CardOrder {
  grams: number
  oz: number
  metal_type: 'gold'|'silver'
  user_message: string
}
@Injectable({
  providedIn: 'root'
})
export class EpinManagerService {
  agxProductId: number = 30584;
  auxProductId: number = 31176;
  silvercardsApiBaseUrl: string = 'https://silvercards.one/api';
  lodeMembersApiBase: string = EXTERNAL_APIS.MEMBERS_PORTAL;
  generateEpinUrl: string = `${EXTERNAL_APIS.GAS_STATION}/epin/create`;
  getEpinInfoUrl: string = `${EXTERNAL_APIS.GAS_STATION}/epin`;
  silvercardPriceTiersUrl: string = `${EXTERNAL_APIS.MEMBERS_PORTAL}/token/{{token}}/getpricetiers`
  redeemEpinUrl: string = `${EXTERNAL_APIS.GAS_STATION}/epin/redeem`;
  gasStationHealth: string = `${EXTERNAL_APIS.GAS_STATION}/health`;

  constructor(private http: HttpClient,
              private membersPortal: MembersPortalService,
              private store: Store<AppState>) {
  }

  async generateEpin(opts) {
    const jwt = await this.membersPortal.returnJwtIfValid();
    let productId;

    switch (opts.tokenSymbol) {
      case 'aux':
        productId = this.auxProductId;
        break;
      case 'agx':
        productId = this.agxProductId;
        break;
      default:
        throw new Error('Not supported token');
    }


    if (!jwt) {
      throw new Error('JWT is not valid');
    }

    const headers = new HttpHeaders({
      Authorization: jwt
    });

    const body = {
      id: productId.toString(),
      firstName: opts.firstname,
      lastName: opts.lastname,
      address: opts.address,
      city: opts.city,
      state: opts.state,
      zip: opts.zip,
      country: opts.country,
      phone: opts.phone,
      email: opts.email,
      tokenAmount: Number(opts.amount),
      token: opts.tokenSymbol,
      returnUrl: 'http://members.agxpay.com',
      ccNumber: opts.ccNumber,
      ccMonth: opts.ccMonth,
      ccYear: opts.ccYear,
      ccCvv: opts.ccCvv,
      promoCode: opts.promoCode,
      quoteId: opts.quoteId
    };

    const response = await this.http.post(this.generateEpinUrl, body, { headers } ).toPromise();

    return response;
  }

  async getEpinInfo(epin: string, token: string) {
    let params = new HttpParams();
    const jwt = await this.membersPortal.returnJwtIfValid();

    if (!jwt) {
      throw new Error('JWT is not valid');
    }

    const headers = new HttpHeaders({
      Authorization: jwt
    });

    params = params.append('token', token.toLowerCase());
    params = params.append('epin', epin);

    const response = await this.http.get(this.getEpinInfoUrl, { headers, params }).pipe(timeout(10000)).toPromise();

    return response;
  }

  async redeemEpin(epin: string, token: string) {
    const sysInfo = await this.store.select(getTokenKeyring, { keyringId: 'SYS' }).pipe(take(1)).toPromise();
    const jwt: any = await this.store.select(getJwtToken).pipe(take(1)).toPromise();
    const headers = new HttpHeaders({
      Authorization: jwt
    });

    if (!jwt) {
      throw new Error('JWT is not valid');
    }

    const body = {
      epin,
      address: sysInfo.address,
      coin: token
    };

    const response = await this.http.post(this.redeemEpinUrl, body, { headers }).toPromise();

    return response;
  }

  addFees(amount) {
    const multiplier = 1.038;
    return amount < 25 ? (amount * multiplier) + 1 : amount * multiplier;
  }

  async getMaxRedeemableTokens() {
    let health;
    const jwt: any = await this.store.select(getJwtToken).pipe(take(1)).toPromise();
    const headers = new HttpHeaders({
      Authorization: jwt
    });

    try {
      health = await this.http.get(this.gasStationHealth, { headers }).toPromise();
    } catch (err) {
      throw err;
    }

    return {
      agx: health.wallet.agxBalance,
      aux: health.wallet.auxBalance
    };
  }

  async getPriceTiers(token: string) {
    const url = this.silvercardPriceTiersUrl.replace('{{token}}', token.toLowerCase());
    let data;
    let publicInfo;
    let activePrice;
    var silverOrGoldPrice;
    var filterTokenRate;

    try {
      data = await this.http.get(url).toPromise();
      data = data.data; 
    } catch(err) {
      Logger.error('Error while fetching price tiers for token ', token);
      return [];
    }
    
    try {
      publicInfo = await this.membersPortal.getPublicInfo();
      silverOrGoldPrice = token.toLocaleLowerCase() === 'agx' ? publicInfo.data.last_silver_usd : publicInfo.data.last_gold_usd;
      filterTokenRate = token.toLocaleLowerCase() === 'agx' ? 'agx_rates' : 'aux_rates';
      publicInfo = publicInfo.data[filterTokenRate]; 
    } catch(err) {
      Logger.error('Error while fetching public prices');
      publicInfo = null;
    }

    return data.map((tier, index) => {
      let range;
      let perc;
      tier.min = Number(tier.min);
      tier.max = Number(tier.max);

      if (index === 0) {
        range = `<${Math.round(tier.max)}`;
      } else if (index === data.length - 1) {
        range = `>${Math.round(tier.min)}`;
      } else {
        range = `${Math.round(tier.min)}-${Math.round(tier.max)}`
      }

      perc = (tier.premium - 1) * 100;
      perc = perc.toFixed(2);
      
      // actual premium (base minus digital discount)
      if(publicInfo !== null) {
        activePrice = publicInfo[index] * silverOrGoldPrice;
        activePrice = activePrice.toFixed(5);
      }
      

      return { range,  rate: activePrice, min: tier.min, max: tier.max, ratePerc: perc };
    });
  }
}
